import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  Autocomplete,
  Grid,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Divider,
  CircularProgress,
  Tooltip,
  useMediaQuery,
  useTheme,
  Collapse,
  alpha,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Description as FileIcon, ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import { useCallback } from "react";
import { debounce } from "lodash";
import {
  getIssuesitemdetail,
  getAllItems,
  ExportItemConsumptionReport,
  getCategories,
  fetchSubCategories,
  fetchItemsByCategoryAndSubcategory,
} from "../../../Services/Services";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import InfiniteScroll from "react-infinite-scroll-component";
import { useLocation } from "react-router-dom";

export default function Item_wise_consumption_report() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expandedItem, setExpandedItem] = useState(null);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [itemLists, setItemLists] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [divisionsLoading, setDivisionsLoading] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [selectedDivision, setSelectedDivision] = useState(null);
  // 🆕 Date Range States
  const [dateRange, setDateRange] = useState("today");
  const [customDateRange, setCustomDateRange] = useState({ from: "", to: "" });
  const [dateMenuAnchor, setDateMenuAnchor] = useState(null);
  const [showCustomDateInputs, setShowCustomDateInputs] = useState(false);
  const [tempCustomDateRange, setTempCustomDateRange] = useState({ from: "", to: "" });

  const [filters, setFilters] = useState({
    date_range: "today",
    item: "",
  });

  const location = useLocation();

  // ===== Date Helper Functions =====
  const formatDateDisplay = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  const getDateRangeDisplay = (rangeType) => {
    const now = new Date();
    const formatDate = (date) => {
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    switch (rangeType) {
      case "today":
        return formatDate(now);
      case "yesterday":
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        return formatDate(yesterday);
      case "this_week":
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
        return `${formatDate(weekStart)} to ${formatDate(now)}`;
      case "previous_week":
        const prevWeekEnd = new Date(now);
        prevWeekEnd.setDate(prevWeekEnd.getDate() - prevWeekEnd.getDay());
        const prevWeekStart = new Date(prevWeekEnd);
        prevWeekStart.setDate(prevWeekStart.getDate() - 6);
        return `${formatDate(prevWeekStart)} to ${formatDate(prevWeekEnd)}`;
      case "this_month":
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return `${formatDate(monthStart)} to ${formatDate(now)}`;
      case "current_year":
        const yearStart = new Date(now.getFullYear(), 0, 1);
        return `${formatDate(yearStart)} to ${formatDate(now)}`;
      case "financial_year":
        let fyStart;
        if (now.getMonth() >= 3) {
          fyStart = new Date(now.getFullYear(), 3, 1);
        } else {
          fyStart = new Date(now.getFullYear() - 1, 3, 1);
        }
        return `${formatDate(fyStart)} to ${formatDate(now)}`;
      default:
        return "";
    }
  };

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await getCategories();
        const data = Array.isArray(res) ? res : res.results || res.data || [];
        setCategories(data);
      } catch (err) {
        console.error("Category fetch error:", err);
        toast.error("Failed to load categories");
      }
    };
    loadCategories();
  }, []);
  useEffect(() => {
    if (!selectedCategory?.id) {
      setSubCategories([]);
      setSelectedSubCategory(null);
      setItemLists([]);
      setSelectedItem(null);
      return;
    }

    const loadSubCategories = async () => {
      try {
        const res = await fetchSubCategories(selectedCategory.id);
        const data = Array.isArray(res) ? res : res.results || res.data || [];
        setSubCategories(data);
        setSelectedSubCategory(null);
        setItemLists([]);
        setSelectedItem(null);
      } catch (err) {
        console.error("Sub‑category fetch error:", err);
        toast.error("Failed to load sub‑categories");
      }
    };

    loadSubCategories();
  }, [selectedCategory]);
  useEffect(() => {
    if (!selectedCategory?.id || !selectedSubCategory?.subcat_id) {
      setItemLists([]);
      setSelectedItem(null);
      return;
    }

    const loadItems = async () => {
      try {
        const res = await fetchItemsByCategoryAndSubcategory(
          selectedCategory.id,
          selectedSubCategory.subcat_id
        );
        const data = Array.isArray(res) ? res : res.results || res.data || [];
        setItemLists(data);
        setSelectedItem(null);
      } catch (err) {
        console.error("Item fetch error:", err);
        toast.error("Failed to load items");
      }
    };

    loadItems();
  }, [selectedSubCategory]);
  // ========== Fetch Item List ==========
  useEffect(() => {
    const fetchItemData = async () => {
      try {
        const response = await getAllItems();
        let itemsData = [];
        if (Array.isArray(response)) {
          itemsData = response;
        } else if (response.results) {
          itemsData = response.results;
        } else if (response.data) {
          itemsData = response.data;
        }
        setItemLists(itemsData || []);
      } catch (error) {
        console.error("Error fetching items:", error);
        toast.error("Failed to load items");
        setItemLists([]);
      }
    };
    fetchItemData();
  }, []);

  // ========== Fetch Items ==========
  const fetchItems = async (pageNumber = 1, append = false) => {
    setLoading(true);
    try {
      const apiFilters = { ...filters };

      // ✅ Division filter - using division_id
      if (selectedDivision?.division_id) {
        apiFilters.division_id = selectedDivision.division_id;
        console.log(
          "🔍 Filtering by division_id:",
          selectedDivision.division_id
        );
        console.log("🔍 Full selected division:", selectedDivision);
      } else {
        console.log("⚠️ No division selected");
      }

      if (selectedCategory?.id) {
        apiFilters.category = selectedCategory.id;
      }
      if (selectedSubCategory?.subcat_id) {
        apiFilters.sub_category = selectedSubCategory.subcat_id;
      }
      if (selectedItem?.id) {
        apiFilters.item = selectedItem.id;
      }

      // 🆕 Date range
      apiFilters.date_range = dateRange;

      if (
        dateRange === "custom" &&
        customDateRange.from &&
        customDateRange.to
      ) {
        apiFilters.from_date = customDateRange.from;
        apiFilters.to_date = customDateRange.to;
      }

      console.log("📤 Item Consumption API Params:", apiFilters);

      const data = await getIssuesitemdetail(pageNumber, apiFilters);

      if (append) {
        setItems((prev) => [...prev, ...(data.results || [])]);
      } else {
        setItems(data.results || []);
      }

      setHasMore(!!data.next);
      setPage(pageNumber);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Failed to load consumption data");
    } finally {
      setLoading(false);
    }
  };

  // 🆕 Excel Export Handler
  const handleExcelExport = async () => {
    setExcelLoading(true);
    try {
      const exportFilters = {};

      // ✅ Division filter - using division_id
      if (selectedDivision?.division_id) {
        exportFilters.division_id = selectedDivision.division_id;
      }

      if (selectedItem?.id) {
        exportFilters.item = selectedItem.id;
      }

      exportFilters.date_range = dateRange;

      if (
        dateRange === "custom" &&
        customDateRange.from &&
        customDateRange.to
      ) {
        exportFilters.from_date = customDateRange.from;
        exportFilters.to_date = customDateRange.to;
      }

      console.log("📥 Exporting Item Consumption with filters:", exportFilters);

      const blob = await ExportItemConsumptionReport(exportFilters);

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;

      const filename = `Item_Consumption_Report_${new Date().getTime()}.xlsx`;
      link.setAttribute("download", filename);

      document.body.appendChild(link);
      link.click();

      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Excel file downloaded successfully!");
    } catch (error) {
      console.error("Export error:", error);

      if (error.response) {
        toast.error(`Export failed: ${error.response.status}`);
      } else if (error.request) {
        toast.error("No response from server");
      } else {
        toast.error("Failed to export Excel file");
      }
    } finally {
      setExcelLoading(false);
    }
  };

  // ========== Filter Effect ==========
  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      fetchItems(1, false);
    }, 500);

    return () => clearTimeout(handler);
  }, [selectedDivision, selectedItem, dateRange, customDateRange]);

  // ========== Infinite Scroll ==========
  const fetchMoreData = () => {
    if (hasMore && !loading) {
      fetchItems(page + 1, true);
    }
  };

  // ========== Reset Handler ==========
  const handleReset = () => {
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    setSelectedItem(null);
    setSelectedDivision(null);
    setSubCategories([]);
    setItemLists([]);
    setFilters({ division_name: "", date_range: "today" });
    setDateRange("today");
    setCustomDateRange({ from: "", to: "" });
    setTempCustomDateRange({ from: "", to: "" });
    setShowCustomDateInputs(false);
    setItems([]);
    setPage(1);
    setHasMore(true);
  };

  // ✅ Handle Custom Date Range Apply
  const handleApplyCustomDateRange = () => {
    if (tempCustomDateRange.from && tempCustomDateRange.to) {
      setCustomDateRange({
        from: tempCustomDateRange.from,
        to: tempCustomDateRange.to,
      });
      setDateRange("custom");
      setFilters((prev) => ({ ...prev, date_range: "custom" }));
      setDateMenuAnchor(null);
      setShowCustomDateInputs(false);
    } else {
      toast.warning("Please select both From and To dates");
    }
  };

  // ✅ Handle Cancel Custom Date Range
  const handleCancelCustomDateRange = () => {
    setTempCustomDateRange({ from: "", to: "" });
    setShowCustomDateInputs(false);
  };

  // ========== Location State ==========
  useEffect(() => {
    if (location.state?.edited) {
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleDebouncedSearch = useCallback(
    debounce(async (type, keyword, parentIds = {}) => {
      try {
        if (!keyword || keyword.trim() === "") {
          // fetch default list (all)
          if (type === "category") {
            const catRes = await getCategories();
            setCategories(
              Array.isArray(catRes.results) ? catRes.results : catRes
            );
          }
          if (type === "sub_category" && parentIds.categoryId) {
            const subRes = await fetchSubCategories(parentIds.categoryId);
            setSubCategories(
              Array.isArray(subRes.results) ? subRes.results : subRes
            );
          }
          if (
            type === "item" &&
            parentIds.categoryId &&
            parentIds.subCategoryId
          ) {
            const itemRes = await fetchItemsByCategoryAndSubcategory(
              parentIds.categoryId,
              parentIds.subCategoryId
            );
            setItemLists(
              Array.isArray(itemRes.results) ? itemRes.results : itemRes
            );
          }
          return;
        }

        // search with keyword
        if (type === "category") {
          const catRes = await getCategories(1, keyword);
          setCategories(
            Array.isArray(catRes.results) ? catRes.results : catRes
          );
        }

        if (type === "sub_category" && parentIds.categoryId) {
          const subRes = await fetchSubCategories(
            parentIds.categoryId,
            1,
            keyword
          );
          setSubCategories(
            Array.isArray(subRes.results) ? subRes.results : subRes
          );
        }

        if (
          type === "item" &&
          parentIds.categoryId &&
          parentIds.subCategoryId
        ) {
          const itemRes = await fetchItemsByCategoryAndSubcategory(
            parentIds.categoryId,
            parentIds.subCategoryId,
            1,
            keyword
          );
          setItemLists(
            Array.isArray(itemRes.results) ? itemRes.results : itemRes
          );
        }
      } catch (err) {
        console.error("Search error:", err);
        toast.error(`Failed to fetch ${type} list`);
      }
    }, 500),
    []
  );

  return (
    <Box
      flexDirection={{ xs: "column", sm: "row" }}
      alignItems={{ xs: "stretch", sm: "center" }}
      sx={{
        mt: isMobile ? 4 : 4,
        pt: 2,
        mb: 2,
        width: "100%",
        gap: 2,
      }}
    >
      <ToastContainer position="top-right" autoClose={3000} />

      {/* 🆕 Filter Section */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* Date Range Dropdown */}
        <Grid item xs={12} sm={2.5}>
          <TextField
            label="Date Range"
            value={
              dateRange === "custom" &&
                customDateRange.from &&
                customDateRange.to
                ? `${formatDateDisplay(
                  customDateRange.from
                )} to ${formatDateDisplay(customDateRange.to)}`
                : dateRange === "today"
                  ? "Today"
                  : dateRange === "yesterday"
                    ? "Yesterday"
                    : dateRange === "this_week"
                      ? "This Week"
                      : dateRange === "previous_week"
                        ? "Previous Week"
                        : dateRange === "this_month"
                          ? "This Month"
                          : dateRange === "current_year"
                            ? "Current Year"
                            : dateRange === "financial_year"
                              ? "Financial Year"
                              : "Select Date Range"
            }
            onClick={(e) => setDateMenuAnchor(e.currentTarget)}
            size="small"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <IconButton size="small" edge="end">
                  <ArrowDropDownIcon />
                </IconButton>
              ),
            }}
            sx={{
              cursor: "pointer",
              "& .MuiInputBase-root": {
                height: 35,
                borderRadius: 0,
              },
              "& .MuiInputLabel-root": {
                fontSize: "0.7rem",
              },
            }}
            fullWidth
          />
        </Grid>

        {/* Category */}
        <Grid item xs={12} sm={2.5}>
          <Autocomplete
            options={categories}
            getOptionLabel={(opt) => opt.name || ""}
            value={selectedCategory}
            onChange={(e, newVal) => {
              setSelectedCategory(newVal);
              setSelectedSubCategory(null);
              setSubCategories([]);
              setSelectedItem(null);
              setItemLists([]);
              if (newVal)
                handleDebouncedSearch("sub_category", "", {
                  categoryId: newVal.id,
                });
            }}
            onInputChange={(e, newVal, reason) => {
              if (reason === "input") handleDebouncedSearch("category", newVal);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Category"
                size="small"
                sx={{
                  "& .MuiInputBase-root": {
                    height: 35,
                    borderRadius: 0,
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.7rem",
                  },
                }}
                fullWidth
              />
            )}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
          />
        </Grid>

        {/* Sub‑Category */}
        {/* <Grid item xs={12} sm={2.5}>
          <Autocomplete
            options={subCategories}
            getOptionLabel={(opt) => opt.name || ""}
            value={selectedSubCategory}
            onChange={(e, newVal) => {
              setSelectedSubCategory(newVal);
              setSelectedItem(null);
              setItemLists([]);
              if (newVal && selectedCategory) {
                handleDebouncedSearch("item", "", {
                  categoryId: selectedCategory.id,
                  subCategoryId: newVal.subcat_id,
                });
              }
            }}
            onInputChange={(e, newVal, reason) => {
              if (reason === "input" && selectedCategory)
                handleDebouncedSearch("sub_category", newVal, {
                  categoryId: selectedCategory.id,
                });
            }}
            disabled={!selectedCategory}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Sub‑Category"
                size="small"
                sx={{
                  "& .MuiInputBase-root": {
                    height: 35,
                    borderRadius: 0,
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.7rem",
                  },
                }}
                fullWidth
              />
            )}
            isOptionEqualToValue={(a, b) => a?.subcat_id === b?.subcat_id}
          />
        </Grid> */}

        {/* Item */}
        <Grid item xs={12} sm={2.5}>
          <Autocomplete
            options={itemLists}
            getOptionLabel={(opt) => opt.name || ""}
            value={selectedItem}
            onChange={(e, newVal) => setSelectedItem(newVal)}
            onInputChange={(e, newVal, reason) => {
              if (reason === "input" && selectedCategory && selectedSubCategory)
                handleDebouncedSearch("item", newVal, {
                  categoryId: selectedCategory.id,
                  subCategoryId: selectedSubCategory.subcat_id,
                });
            }}
            disabled={!selectedSubCategory}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Item"
                size="small"
                sx={{
                  "& .MuiInputBase-root": {
                    height: 35,
                    borderRadius: 0,
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.7rem",
                  },
                }}
                fullWidth
              />
            )}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
          />
        </Grid>


        {/* Reset Button */}
        <Grid item xs={12} sm={1.5}>
          <Button
            variant="contained"
            onClick={handleReset}
            sx={{
              height: "35px",
              borderRadius: "0",
              fontSize: "0.7rem",
            }}
            fullWidth
          >
            Reset
          </Button>
        </Grid>

        {/* Excel Export Button */}
        <Grid item xs={12} sm={0.5}>
          <Tooltip
            title={excelLoading ? "Exporting..." : "Download Excel"}
            arrow
          >
            <span>
              <IconButton
                color="success"
                size="small"
                onClick={handleExcelExport}
                disabled={excelLoading}
                sx={{
                  border: `1px solid ${theme.palette.success.main}`,
                  borderRadius: "4px",
                  height: "35px",
                  width: "35px",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.success.main, 0.08),
                  },
                }}
              >
                {excelLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <FileIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
      </Grid>

      {/* Date Range Menu - UPDATED */}
      <Menu
        anchorEl={dateMenuAnchor}
        open={Boolean(dateMenuAnchor)}
        onClose={() => {
          setDateMenuAnchor(null);
          setShowCustomDateInputs(false);
          setTempCustomDateRange({ from: "", to: "" });
        }}
        PaperProps={{
          sx: { width: 350, maxHeight: 500, bgcolor: theme.palette.background.paper },
        }}
      >
        {[
          { key: "today", label: "Today" },
          { key: "yesterday", label: "Yesterday" },
          { key: "this_week", label: "This Week" },
          { key: "previous_week", label: "Previous Week" },
          { key: "this_month", label: "This Month" },
          { key: "current_year", label: "Current Year" },
          { key: "financial_year", label: "Financial Year" },
        ].map((range) => (
          <MenuItem
            key={range.key}
            onClick={() => {
              setDateRange(range.key);
              setFilters((prev) => ({ ...prev, date_range: range.key }));
              setCustomDateRange({ from: "", to: "" });
              setDateMenuAnchor(null);
              setShowCustomDateInputs(false);
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Typography variant="body2" color="text.primary">{range.label}</Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.65rem" }}
              >
                {getDateRangeDisplay(range.key)}
              </Typography>
            </Box>
          </MenuItem>
        ))}

        <Divider />

        {/* ✅ Custom Date Range - Expandable */}
        <MenuItem
          onClick={() => setShowCustomDateInputs(!showCustomDateInputs)}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: showCustomDateInputs
              ? isDark ? alpha(theme.palette.primary.main, 0.15) : "#e3f2fd"
              : "inherit",
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: showCustomDateInputs ? 600 : 400, color: "text.primary" }}
          >
            Custom Date Range
          </Typography>
          {showCustomDateInputs ? <ExpandLess /> : <ExpandMore />}
        </MenuItem>

        {/* ✅ Both Date Pickers Together */}
        <Collapse in={showCustomDateInputs} timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 2,
              py: 2,
              bgcolor: isDark ? alpha(theme.palette.common.white, 0.04) : "#f5f5f5",
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
              Select Date Range
            </Typography>

            {/* From and To Date Inputs Side by Side */}
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <TextField
                label="From Date"
                type="date"
                size="small"
                value={tempCustomDateRange.from}
                onChange={(e) =>
                  setTempCustomDateRange((prev) => ({
                    ...prev,
                    from: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: tempCustomDateRange.to || new Date().toISOString().split("T")[0],
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    height: 40,
                    fontSize: "0.75rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.75rem",
                  },
                }}
              />

              <TextField
                label="To Date"
                type="date"
                size="small"
                value={tempCustomDateRange.to}
                onChange={(e) =>
                  setTempCustomDateRange((prev) => ({
                    ...prev,
                    to: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: tempCustomDateRange.from || undefined,
                  max: new Date().toISOString().split("T")[0],
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    height: 40,
                    fontSize: "0.75rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.75rem",
                  },
                }}
              />
            </Box>

            {/* Selected Range Preview */}
            {tempCustomDateRange.from && tempCustomDateRange.to && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1.5,
                  p: 1,
                  bgcolor: isDark ? alpha(theme.palette.success.main, 0.15) : "#e8f5e9",
                  borderRadius: 1,
                  textAlign: "center",
                  color: theme.palette.success.main,
                  fontWeight: 500,
                }}
              >
                📅 {formatDateDisplay(tempCustomDateRange.from)} to{" "}
                {formatDateDisplay(tempCustomDateRange.to)}
              </Typography>
            )}

            {/* Apply and Cancel Buttons */}
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCancelCustomDateRange}
                sx={{
                  fontSize: "0.7rem",
                  textTransform: "none",
                  borderRadius: 1,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleApplyCustomDateRange}
                disabled={!tempCustomDateRange.from || !tempCustomDateRange.to}
                sx={{
                  fontSize: "0.7rem",
                  textTransform: "none",
                  borderRadius: 1,
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Menu>

      {/* ✅ Mobile/Desktop Conditional Rendering */}
      <InfiniteScroll
        dataLength={items.length}
        next={fetchMoreData}
        hasMore={hasMore}
        height={"70vh"}
        loader={
          loading && (
            <Box sx={{ textAlign: "center", p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )
        }
        endMessage={
          <Typography
            sx={{ textAlign: "center", p: 2, color: "text.secondary" }}
          >
            {items.length === 0 ? "No data found" : "No more data"}
          </Typography>
        }
      >
        {isMobile ? (
          /* 📱 MOBILE VIEW - Collapsible Cards */
          <Box sx={{ bgcolor: theme.palette.background.paper }}>
            {items.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Typography variant="h6" color="text.secondary">
                  {loading ? "Loading..." : "No data found"}
                </Typography>
              </Box>
            ) : (
              items.map((item, index) => (
                <Box key={item.id}>
                  {/* Item Header Card */}
                  <Box
                    onClick={() =>
                      setExpandedItem(
                        expandedItem === item.slip_no ? null : item.slip_no
                      )
                    }
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: 1.5,
                      cursor: "pointer",
                      bgcolor: index % 2 === 0
                        ? isDark ? alpha(theme.palette.common.white, 0.04) : "#fafafa"
                        : theme.palette.background.paper,
                      "&:active": {
                        bgcolor: isDark ? alpha(theme.palette.primary.main, 0.2) : "#e3f2fd",
                      },
                      borderLeft: `4px solid ${theme.palette.primary.main}`,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.7rem",
                            color: "text.disabled",
                            minWidth: 25,
                          }}
                        >
                          #{index + 1}
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: "bold",
                            fontSize: "0.85rem",
                            flex: 1,
                            color: "text.primary",
                          }}
                        >
                          {item.slip_no || "N/A"}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color: "text.secondary",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <span style={{ fontWeight: 500 }}>
                          {item.item_name || item.item_description || "N/A"}
                        </span>
                        <span style={{ color: theme.palette.text.disabled }}>•</span>
                        <span
                          style={{
                            fontSize: "0.72rem",
                            fontWeight: 600,
                            color: theme.palette.success.main,
                          }}
                        >
                          Qty: {item.issue_qty || 0}
                        </span>
                      </Typography>
                    </Box>
                    <IconButton size="small" sx={{ ml: 1 }}>
                      {expandedItem === item.slip_no ? (
                        <ExpandLess fontSize="small" />
                      ) : (
                        <ExpandMore fontSize="small" />
                      )}
                    </IconButton>
                  </Box>

                  {/* Item Details - Collapsible */}
                  <Collapse
                    in={expandedItem === item.slip_no}
                    timeout="auto"
                    unmountOnExit
                  >
                    <Box
                      sx={{
                        px: 1.5,
                        py: 1,
                        bgcolor: index % 2 === 0
                          ? isDark ? alpha(theme.palette.common.white, 0.03) : "#f8f9fa"
                          : isDark ? alpha(theme.palette.common.white, 0.02) : "#fafafa",
                        borderLeft: `4px solid ${theme.palette.primary.main}`,
                      }}
                    >
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 0.8,
                        }}
                      >
                        {/* Slip No */}
                        <Box sx={{ gridColumn: "1 / -1" }}>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "text.disabled",
                              letterSpacing: "0.5px",
                            }}
                          >
                            SLIP NO
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              color: theme.palette.primary.main,
                            }}
                          >
                            {item.slip_no || "-"}
                          </Typography>
                        </Box>

                        {/* Item Name */}
                        <Box sx={{ gridColumn: "1 / -1" }}>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "text.disabled",
                              letterSpacing: "0.5px",
                            }}
                          >
                            ITEM NAME
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.72rem",
                              fontWeight: 500,
                              color: "text.primary",
                            }}
                          >
                            {item.item_description || item.item_name || "-"}
                          </Typography>
                        </Box>

                        {/* Category */}
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "text.disabled",
                              letterSpacing: "0.5px",
                            }}
                          >
                            CATEGORY
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              fontWeight: 500,
                              color: "text.primary",
                            }}
                          >
                            {item.category_name || "-"}
                          </Typography>
                        </Box>

                        {/* Unit */}
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "text.disabled",
                              letterSpacing: "0.5px",
                            }}
                          >
                            UNIT
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              fontWeight: 500,
                              color: "text.primary",
                            }}
                          >
                            {item.uom_name || "-"}
                          </Typography>
                        </Box>

                        {/* Total Qty Issued */}
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "text.disabled",
                              letterSpacing: "0.5px",
                            }}
                          >
                            TOTAL QTY ISSUED
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.72rem",
                              fontWeight: 600,
                              color: theme.palette.success.main,
                            }}
                          >
                            {item.issue_qty || 0}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Collapse>
                  <Divider sx={{ borderColor: theme.palette.divider }} />
                </Box>
              ))
            )}
          </Box>
        ) : (
          /* 💻 DESKTOP VIEW - Original Table */
          <TableContainer
            component={Paper}
            id="item-table"
            sx={{
              maxHeight: "72vh",
              overflowX: { xs: "auto", sm: "unset" },
              overflowY: "auto",
              width: "100%",
              bgcolor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Table
              stickyHeader
              size="small"
              sx={{
                minWidth: "max-content",
                "& td, & th": {
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  color: theme.palette.text.primary,
                },
              }}
            >
              <TableHead
                sx={{
                  "& th": {
                    fontWeight: "bold",
                    color: isDark ? theme.palette.primary.light : "#333",
                    backgroundColor: isDark ? theme.palette.grey[900] : "#f5f5f5",
                  },
                }}
              >
                <TableRow>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      backgroundColor: isDark ? theme.palette.grey[900] : "#f5f5f5",
                      zIndex: 3,
                      padding: "0px",
                    }}
                  >
                    S. No.
                  </TableCell>
                  <TableCell>Slip No.</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Item Name</TableCell>
                  <TableCell align="right">Total Qty Issued</TableCell>
                  <TableCell align="right">Unit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      {loading ? "Loading..." : "No data found"}
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item, index) => (
                    <TableRow
                      hover
                      key={item.id}
                      sx={{
                        backgroundColor:
                          index % 2 === 0
                            ? isDark ? alpha(theme.palette.common.white, 0.03) : "#fafafa"
                            : theme.palette.background.paper,
                        "&:hover": {
                          backgroundColor: isDark ? theme.palette.grey[800] : "#e3f2fd",
                          "& td": {
                            backgroundColor: isDark ? theme.palette.grey[800] : "#e3f2fd !important",
                          },
                        },
                        "& td": {
                          paddingTop: "2px !important",
                          paddingBottom: "2px !important",
                          fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          color: theme.palette.text.primary,
                        },
                      }}
                    >
                      {/* Serial Number */}
                      <TableCell
                        align="center"
                        sx={{
                          position: "sticky",
                          left: 0,
                          backgroundColor:
                            index % 2 === 0
                              ? isDark ? alpha(theme.palette.common.white, 0.03) : "#fafafa"
                              : theme.palette.background.paper,
                          minWidth: 50,
                        }}
                      >
                        {index + 1}
                      </TableCell>

                      {/* Slip Number */}
                      <TableCell sx={{ minWidth: 100 }}>
                        {item.slip_no || "-"}
                      </TableCell>

                      {/* Category */}
                      <TableCell sx={{ minWidth: 120 }}>
                        {item.category_name || "-"}
                      </TableCell>

                      {/* Item Description/Name */}
                      <TableCell sx={{ minWidth: 200 }}>
                        {item.item_description || item.item_name || "-"}
                      </TableCell>

                      {/* Issue Qty */}
                      <TableCell align="right" sx={{ minWidth: 80 }}>
                        {item.issue_qty || 0}
                      </TableCell>

                      {/* UOM */}
                      <TableCell align="center" sx={{ minWidth: 80 }}>
                        {item.uom_name || "-"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </InfiniteScroll>
    </Box>
  );
}